import i18n from '../../i18n';
import { useWorkflowStore } from '../../pages/WorkflowBuilder/store';

export interface TourStep {
  id: string;
  phase: number;
  /** CSS selector for driver.js highlight. Omit → floating card (no overlay). */
  element?: string;
  /** CSS selector for non-blocking highlight ring on action steps. */
  highlightElement?: string;
  /** CSS selector for a second element to keep visible (e.g. canvas during drag steps). */
  secondaryHighlightElement?: string;
  title: string;
  description: string;
  side?: 'top' | 'bottom' | 'left' | 'right';
  align?: 'start' | 'center' | 'end';
  /** Action step: rendered as floating card, user interacts then clicks Next */
  isAction?: boolean;
  /** Navigate to this path when moving to this step */
  navigateTo?: string;
  /** Returns true when the user has completed the required action for this step */
  validate?: () => boolean;
}

const t = (key: string): string =>
  i18n.t(key, { ns: 'workflow' }) as string;

/** Helper: read current workflow state from Zustand (outside React) */
const getStore = () => useWorkflowStore.getState();

/** Check if a node of given type exists on canvas */
const hasNode = (type: string): boolean =>
  getStore().nodes.some((n) => n.type === type);

/** Count edges on canvas (excluding zone-internal wiring) */
const edgeCount = (): number => getStore().edges.length;

/**
 * Tour Steps — 18 total across 4 phases
 *
 * Each action step = ONE clear task for the user.
 * Sub-steps (drag → config → close) are described within a single step.
 *
 * PHASE 1 (3): Welcome → Node Library → Canvas
 * PHASE 2 (7): Name → Select Account & Config → Post to Profile & Config
 *              → Join Groups & Config → Post to Group & Config → Connect edges → Save & Activate
 * PHASE 3 (5): New + Name → Comment on Post & Config → Share URL & Config
 *              → Interact & Config → Connect + Save + Activate
 * PHASE 4 (3): Navigate Schedule → View Schedule → Congratulations
 *
 * Guide steps (has element): use driver.js highlight + overlay
 * Action steps (isAction): floating card in bottom-left, NO overlay
 */
export function getTourSteps(): TourStep[] {
  return [
    // ═══════════════════════════════════════════════════════════════
    // PHASE 1: Introduction (3 steps)
    // ═══════════════════════════════════════════════════════════════
    {
      id: 'welcome',
      phase: 1,
      title: t('tour_welcome_title'),
      description: t('tour_welcome_desc'),
    },
    {
      id: 'node-library',
      phase: 1,
      element: '[data-tour="node-library"]',
      title: t('tour_nodeLibrary_title'),
      description: t('tour_nodeLibrary_desc'),
      side: 'right',
    },
    {
      id: 'canvas-intro',
      phase: 1,
      element: '[data-tour="canvas-area"]',
      title: t('tour_canvas_title'),
      description: t('tour_canvas_desc'),
      side: 'left',
    },

    // ═══════════════════════════════════════════════════════════════
    // PHASE 2: Build Community — 7 steps
    // ═══════════════════════════════════════════════════════════════

    // Step 1: Name the workflow
    {
      id: 'p2-name',
      phase: 2,
      title: t('tour_p2_name_title'),
      description: t('tour_p2_name_desc'),
      isAction: true,
      highlightElement: '[data-tour="workflow-name-input"]',
      validate: () => {
        const name = getStore().workflowName.trim();
        return name !== '' && name !== 'Untitled Workflow';
      },
    },

    // Step 2: Click Select Account → config → close
    {
      id: 'p2-select-account',
      phase: 2,
      title: t('tour_p2_selectAccount_title'),
      description: t('tour_p2_selectAccount_desc'),
      isAction: true,
      highlightElement: '[data-tour="data-zone"]',
      validate: () => {
        const node = getStore().nodes.find((n) => n.type === 'account_selector');
        const cfg = (node?.data as any)?.account_selector_config || (node?.data as any)?.account_selector;
        return cfg?.selected_accounts?.length > 0;
      },
    },

    // Step 3: Drag Post to Profile → config → close
    {
      id: 'p2-post-profile',
      phase: 2,
      title: t('tour_p2_postProfile_title'),
      description: t('tour_p2_postProfile_desc'),
      isAction: true,
      highlightElement: '[data-tour="category-with-profile"]',
      secondaryHighlightElement: '[data-tour="canvas-area"]',
      validate: () => hasNode('post_to_profile'),
    },

    // Step 4: Drag Join Groups → config → close
    {
      id: 'p2-join-groups',
      phase: 2,
      title: t('tour_p2_joinGroups_title'),
      description: t('tour_p2_joinGroups_desc'),
      isAction: true,
      highlightElement: '[data-tour="category-with-group"]',
      secondaryHighlightElement: '[data-tour="canvas-area"]',
      validate: () => hasNode('join_groups'),
    },

    // Step 5: Drag Post to Group → config → close
    {
      id: 'p2-post-group',
      phase: 2,
      title: t('tour_p2_postGroup_title'),
      description: t('tour_p2_postGroup_desc'),
      isAction: true,
      highlightElement: '[data-tour="category-with-group"]',
      secondaryHighlightElement: '[data-tour="canvas-area"]',
      validate: () => hasNode('post_to_group'),
    },

    // Step 6: Connect all edges
    {
      id: 'p2-connect',
      phase: 2,
      title: t('tour_p2_connect_title'),
      description: t('tour_p2_connect_desc'),
      isAction: true,
      highlightElement: '[data-tour="canvas-area"]',
      validate: () => edgeCount() >= 3,
    },

    // Step 7: Save + Activate
    {
      id: 'p2-save-activate',
      phase: 2,
      title: t('tour_p2_saveActivate_title'),
      description: t('tour_p2_saveActivate_desc'),
      isAction: true,
      highlightElement: '[data-tour="btn-save"]',
      secondaryHighlightElement: '[data-tour="btn-activate"]',
      validate: () => !getStore().isDirty,
    },

    // ═══════════════════════════════════════════════════════════════
    // PHASE 3: Engage & Spread — 5 steps
    // ═══════════════════════════════════════════════════════════════

    // Step 1: Click New + Name + Select Account & config
    {
      id: 'p3-new-and-setup',
      phase: 3,
      title: t('tour_p3_newAndSetup_title'),
      description: t('tour_p3_newAndSetup_desc'),
      isAction: true,
      highlightElement: '[data-tour="btn-new"]',
      validate: () => {
        const s = getStore();
        const name = s.workflowName.trim();
        const node = s.nodes.find((n) => n.type === 'account_selector');
        const cfg = (node?.data as any)?.account_selector_config || (node?.data as any)?.account_selector;
        return name !== '' && name !== 'Untitled Workflow' && cfg?.selected_accounts?.length > 0;
      },
    },

    // Step 2: Drag Comment on Post → config → close
    {
      id: 'p3-comment-on-post',
      phase: 3,
      title: t('tour_p3_commentOnPost_title'),
      description: t('tour_p3_commentOnPost_desc'),
      isAction: true,
      highlightElement: '[data-tour="category-with-post"]',
      secondaryHighlightElement: '[data-tour="canvas-area"]',
      validate: () => hasNode('comment_on_post'),
    },

    // Step 3: Drag Share URL → config → close
    {
      id: 'p3-share-url',
      phase: 3,
      title: t('tour_p3_shareUrl_title'),
      description: t('tour_p3_shareUrl_desc'),
      isAction: true,
      highlightElement: '[data-tour="category-with-profile"]',
      secondaryHighlightElement: '[data-tour="canvas-area"]',
      validate: () => hasNode('share_url'),
    },

    // Step 4: Drag Interact → config → close
    {
      id: 'p3-interact',
      phase: 3,
      title: t('tour_p3_interact_title'),
      description: t('tour_p3_interact_desc'),
      isAction: true,
      highlightElement: '[data-tour="category-with-profile"]',
      secondaryHighlightElement: '[data-tour="canvas-area"]',
      validate: () => hasNode('interact'),
    },

    // Step 5: Connect + Save + Activate
    {
      id: 'p3-save-activate',
      phase: 3,
      title: t('tour_p3_saveActivate_title'),
      description: t('tour_p3_saveActivate_desc'),
      isAction: true,
      highlightElement: '[data-tour="btn-save"]',
      secondaryHighlightElement: '[data-tour="btn-activate"]',
      validate: () => edgeCount() >= 3 && !getStore().isDirty,
    },

    // ═══════════════════════════════════════════════════════════════
    // PHASE 4: View Schedule — 3 steps
    // ═══════════════════════════════════════════════════════════════

    // Step 1: Navigate to Schedule
    {
      id: 'p4-nav-schedule',
      phase: 4,
      title: t('tour_p4_navSchedule_title'),
      description: t('tour_p4_navSchedule_desc'),
      isAction: true,
      highlightElement: '[data-tour="nav-schedules"]',
      navigateTo: '/schedules',
    },

    // Step 2: View the calendar
    {
      id: 'p4-view-calendar',
      phase: 4,
      element: '[data-tour="schedule-toggle"]',
      title: t('tour_p4_viewCalendar_title'),
      description: t('tour_p4_viewCalendar_desc'),
      side: 'bottom',
    },

    // Step 3: Congratulations
    {
      id: 'p4-complete',
      phase: 4,
      title: t('tour_p4_complete_title'),
      description: t('tour_p4_complete_desc'),
    },
  ];
}

export const TOUR_TOTAL_STEPS = 18;
